import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// #278. This panel is already scoped to one item, so the Qty line can resolve the
// line it is actually describing instead of taking whichever came back first. The
// [0] fallback stays for off-catalog rows, whose item_id is nullable by design.
function resolveRequestLine(req, itemId) {
  const lines = req?.material_request_items;
  if (!Array.isArray(lines) || lines.length === 0) return null;
  if (itemId != null) {
    const matched = lines.find(l => l.item_id === itemId);
    if (matched) return matched;
  }
  return lines[0];
}

function RequestList({
  requests,
  itemId,
  compact,
  canManage,
  onViewDetails,
}) {
  return requests.map(req => (
    <div key={req.id} style={{ marginBottom: compact ? 8 : 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#412402' }}>{req.request_number}</div>
      <div style={{ fontSize: 11, color: '#633806', marginBottom: 2 }}>
        Qty: {resolveRequestLine(req, itemId)?.qty_requested ?? '—'} · {fmtDate(req.created_at)}
      </div>
      <div style={{ fontSize: 11, color: '#633806', marginBottom: 6 }}>
        {req.status === 'pending'
          ? 'Pending manager approval'
          : req.status === 'approved'
          ? 'Approved — purchasing to order'
          : 'Ordered — awaiting receipt'}
      </div>
      {(req.status === 'pending' || req.status === 'ordered') && canManage && (
        <button
          onClick={() => onViewDetails(req)}
          style={{
            width: '100%',
            padding: compact ? '8px 0' : '7px 0',
            background: '#0F0F0F',
            border: 'none',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 700,
            color: 'white',
            cursor: 'pointer',
            marginBottom: 4,
          }}
        >
          View details →
        </button>
      )}
      {req.status === 'ordered' && (
        <span
          style={{
            display: 'inline-block',
            fontSize: 11,
            color: '#085041',
            background: '#E1F5EE',
            padding: '2px 8px',
            borderRadius: 6,
            marginBottom: 4,
          }}
        >
          Ordered — awaiting receipt
        </span>
      )}
      {req.tracking_number && (
        <div
          style={{
            background: '#E6F1FB',
            borderRadius: 6,
            padding: '6px 10px',
            marginTop: 4,
            marginBottom: 4,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: '#0C447C',
              textTransform: 'uppercase',
              letterSpacing: '.4px',
              marginBottom: 2,
            }}
          >
            Tracking
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#0C447C' }}>
            {req.tracking_carrier ? req.tracking_carrier + ' · ' : ''}
            {req.tracking_number}
          </div>
          {req.tracking_eta && (
            <div style={{ fontSize: 11, color: '#185FA5', marginTop: 1 }}>
              ETA:{' '}
              {new Date(req.tracking_eta).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          )}
        </div>
      )}
      {/* #592, gate 391 D18 -- THE "Cancel request" BUTTON THAT WAS HERE IS
          REMOVED. It ended a request in one tap with no confirmation -- even one
          Purchasing had already ORDERED -- wrote `closed` rather than
          `cancelled`, and never told Purchasing, so an order the vendor was
          shipping dropped off Purchasing's list. A request now ends in exactly
          three places, each with a record: the asker withdraws it (My
          requests), an approver denies it (Requests inbox), Purchasing cancels
          it (Purchasing Queue). View details, above, opens it where those are. */}
      <hr style={{ border: 'none', borderTop: '0.5px solid #EF9F27', margin: '6px 0' }} />
    </div>
  ));
}

export default function MaterialRequestsPanel({
  itemId,
  itemName,
  branchId,
  compact = false,
  canManage,
  setActive,
  setOpenRequestId,
  onRequestsChanged,
}) {
  const [requests, setRequests] = useState([]);

  const load = useCallback(async () => {
    if (!itemId || !branchId) return;
    const { data } = await supabase
      .from('material_requests')
      .select(`
        id, request_number, status, branch_id, created_at, notes, requested_by, vendor_id,
        tracking_carrier, tracking_number, tracking_eta,
        material_request_items ( id, item_id, qty_requested, qty_received )
      `)
      .eq('branch_id', branchId)
      .in('status', ['pending', 'approved', 'ordered'])
      .order('created_at', { ascending: false });

    const filtered = (data || []).filter(r =>
      r.material_request_items?.some(i => i.item_id === itemId)
    );
    setRequests(filtered);
    if (onRequestsChanged) onRequestsChanged(filtered);
  }, [itemId, branchId, onRequestsChanged]);

  useEffect(() => {
    load();
  }, [load]);

  // Replaces the old "Mark ordered" button, which quietly set the request to
  // "approved" (not "ordered") and emailed purchasing directly — a second,
  // weaker ordering path that bypassed PurchasingQueuePage.js, the one real
  // place orders get placed with a vendor and price attached. This just sends
  // the person to the real request, reusing the same "open this exact request"
  // hand-off App.js already uses for notification links.
  function handleViewDetails(req) {
    if (setOpenRequestId) setOpenRequestId(req.id);
    if (setActive) setActive('requests_inbox');
  }

  const hasRequests = requests.length > 0;

  return (
    <div
      style={{
        background: hasRequests ? '#FAEEDA' : 'white',
        border: `1px solid ${hasRequests ? '#EF9F27' : compact ? '#E8E8E8' : 'var(--border)'}`,
        borderRadius: compact ? 10 : 'var(--radius-lg)',
        padding: compact ? '10px 12px' : '12px 14px',
      }}
    >
      <div
        style={{
          fontSize: compact ? 10 : 11,
          fontWeight: 700,
          color: hasRequests ? '#633806' : compact ? '#9A9A9A' : 'var(--gray-mid)',
          textTransform: 'uppercase',
          letterSpacing: compact ? undefined : '0.05em',
          marginBottom: compact ? 6 : 8,
        }}
      >
        Restock requests
      </div>

      {!hasRequests ? (
        <div style={{ fontSize: 12, color: compact ? '#9A9A9A' : 'var(--gray-lt)', marginBottom: 8 }}>
          No open requests for this item.
        </div>
      ) : (
        <RequestList
          requests={requests}
          itemId={itemId}
          compact={compact}
          canManage={canManage}
          onViewDetails={handleViewDetails}
        />
      )}
    </div>
  );
}